import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { STATUS_LABELS, ENTRY_STATUS_LABELS, PURPLE, NAVY } from "./giveawayUtils";

// Shared bits for the Giveaways screens: status pills, number tiles and the
// one confirm dialog every risky button goes through.

const PILL_STYLES = {
  draft: { background: "#E5E7EB", color: "#374151", border: "1px solid #D1D5DB" },
  scheduled: { background: PURPLE, color: "#fff", border: `1px solid ${PURPLE}` },
  active: { background: PURPLE, color: "#fff", border: `1px solid ${PURPLE}` },
  paused: { background: "#F59E0B", color: NAVY, border: "1px solid #F59E0B" },
  closed: { background: NAVY, color: "#fff", border: "1px solid rgba(140, 80, 253, 0.45)" },
  drawn: { background: NAVY, color: "#fff", border: "1px solid rgba(140, 80, 253, 0.45)" },
  completed: { background: "transparent", color: PURPLE, border: `1.5px solid ${PURPLE}` },
  archived: { background: "#F3F4F6", color: "#9CA3AF", border: "1px solid #E5E7EB" },
};

const ENTRY_PILL_STYLES = {
  valid: PILL_STYLES.active,
  pending_review: PILL_STYLES.paused,
  duplicate: PILL_STYLES.draft,
  disqualified: PILL_STYLES.closed,
};

const pillBase = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  lineHeight: 1.5,
};

export const StatusPill = ({ status, paused }) => {
  const key = paused && (status === "active" || status === "scheduled") ? "paused" : status;
  return (
    <span style={{ ...pillBase, ...(PILL_STYLES[key] || PILL_STYLES.draft) }}>
      {STATUS_LABELS[key] || status || "Unknown"}
    </span>
  );
};

export const EntryPill = ({ status }) => (
  <span style={{ ...pillBase, ...(ENTRY_PILL_STYLES[status] || PILL_STYLES.draft) }}>
    {ENTRY_STATUS_LABELS[status] || status || "Unknown"}
  </span>
);

export const Tile = ({ label, value, icon, sub, cols = "col-xl-2 col-md-4 col-6" }) => (
  <div className={cols}>
    <div className="p-16 radius-8 bg-neutral-50 h-100">
      <div className="d-flex align-items-center gap-2 mb-1">
        {icon ? <Icon icon={icon} className="text-secondary-light" /> : null}
        <span className="text-sm text-secondary-light">{label}</span>
      </div>
      <h5 className="mb-0 fw-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value == null ? "0" : value}
      </h5>
      {sub ? <div className="text-xs text-secondary-light mt-1">{sub}</div> : null}
    </div>
  </div>
);

export const SectionCard = ({ title, subtitle, actions, children }) => (
  <div className="card radius-12 mb-24">
    <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-start justify-content-between flex-wrap gap-2">
      <div style={{ minWidth: 0 }}>
        <h6 className="mb-1 fw-semibold">{title}</h6>
        {subtitle ? (
          <p className="text-sm text-secondary-light mb-0" style={{ maxWidth: 760 }}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? <div className="d-flex align-items-center gap-2 flex-wrap">{actions}</div> : null}
    </div>
    <div className="card-body p-24">{children}</div>
  </div>
);

/**
 * One confirm dialog for every risky action.
 *   fields: [{ name, label, type: "text" | "textarea", required, minLength, placeholder, help }]
 *   typeToConfirm: the exact word the person must type (for example "DRAW").
 *   onConfirm(values) may return a promise; the dialog stays open while it runs
 *   and closes only if it resolves to something other than false.
 */
export const ActionModal = ({
  show,
  title,
  body,
  confirmLabel = "Confirm",
  danger = false,
  fields = [],
  typeToConfirm,
  onConfirm,
  onClose,
}) => {
  const [values, setValues] = useState({});
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (show) {
      const init = {};
      fields.forEach((f) => {
        init[f.name] = f.initial || "";
      });
      setValues(init);
      setTyped("");
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const fieldOk = (f) => {
    const v = (values[f.name] || "").trim();
    if (f.required && !v) return false;
    if (f.minLength && v && v.length < f.minLength) return false;
    return true;
  };
  const ready =
    fields.every(fieldOk) && (!typeToConfirm || typed.trim() === typeToConfirm) && !busy;

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const out = await onConfirm(values);
      if (out !== false) onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title as="h6" className="fw-semibold mb-0">
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {body ? <div className="text-sm mb-16">{body}</div> : null}
        {fields.map((f) => (
          <div className="mb-16" key={f.name}>
            <label className="form-label text-sm fw-semibold">
              {f.label}
              {f.required ? <span style={{ color: "#FA69B9" }}> *</span> : null}
            </label>
            {f.type === "textarea" ? (
              <textarea
                className="form-control"
                rows={3}
                value={values[f.name] || ""}
                placeholder={f.placeholder || ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            ) : (
              <input
                type="text"
                className="form-control"
                value={values[f.name] || ""}
                placeholder={f.placeholder || ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            )}
            {f.help || f.minLength ? (
              <div className="text-xs text-secondary-light mt-1">
                {f.help}
                {f.minLength ? ` At least ${f.minLength} characters.` : ""}
              </div>
            ) : null}
          </div>
        ))}
        {typeToConfirm ? (
          <div className="mb-8">
            <label className="form-label text-sm fw-semibold">
              Type {typeToConfirm} to confirm
            </label>
            <input
              type="text"
              className="form-control"
              value={typed}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={`btn btn-sm ${danger ? "btn-danger" : "btn-primary"}`}
          disabled={!ready}
          onClick={submit}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

// A short list of strings, used for blockers, reasons and notes.
export const PlainList = ({ items, icon = "mdi:circle-small" }) => (
  <ul className="mb-0 ps-0" style={{ listStyle: "none" }}>
    {items.map((x, i) => (
      <li key={i} className="d-flex align-items-start gap-1 text-sm mb-4">
        <Icon icon={icon} style={{ flexShrink: 0, marginTop: 3 }} />
        <span>{x}</span>
      </li>
    ))}
  </ul>
);
